import { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { Box, Divider, Skeleton, Stack, Tooltip, Typography, TextField, Button, DatePicker, Switch, FormControlLabel } from '../../../ui/core';
import { InfoIcon } from '../../../ui/icons';

// Promo start/end dates are picked as bare calendar days but must resolve to a
// real instant. We anchor them to Hitch's home timezone (US Central) so a date
// means what the operator setting it intends — "starts 8/1" is 12:00am Central
// on 8/1, not midnight UTC (which would be ~7pm Central the day before). DST is
// handled automatically by moment-timezone.
const PROMO_TZ = 'America/Chicago';

// Global theme forces square inputs (MuiOutlinedInput borderRadius: 0); soften
// just this form's fields rather than changing that app-wide. Placeholders also
// inherit the theme's 500 weight, which reads as heavy — quiet them down.
const roundedField = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& input::placeholder': { color: 'text.inactive', opacity: 1, fontWeight: 400 },
};

// Two-column layout for the paired settings — 1 column on phones.
const twoColGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
  gap: '18px',
};

// Converts a text input to a number, or null when blank so the backend
// treats the bound as "unset" rather than 0.
const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const toDateInput = (v) => (v ? moment(v) : null);

// Derive the editable field state from a promotion (or null = new). Kept at
// module scope so it's a stable dependency and can re-seed the form when the
// selected promotion changes without remounting it.
export const deriveState = (initial) => {
  const option = (initial && initial.options && initial.options[0]) || {};
  return {
    code: initial?.code || '',
    description: initial?.description || '',
    subtitle: initial?.subtitle || '',
    amount: option.amount ?? '',
    amountType: option.type === 'percent' ? 'percent' : 'usd',
    startsOn: initial?.startsOn ? moment.tz(initial.startsOn, PROMO_TZ).format('YYYY-MM-DD') : '',
    endsOn: initial?.endsOn ? moment.tz(initial.endsOn, PROMO_TZ).format('YYYY-MM-DD') : '',
    beforeEventCount: initial?.beforeEventCount ?? '',
    afterEventCount: initial?.afterEventCount ?? '',
    minimumPurchaseAmount: initial?.minimumPurchaseAmount ?? '',
    maximumDiscountAmount: initial?.maximumDiscountAmount ?? '',
    maxUses: initial?.maxUses ?? '',
    disableGrossUp: initial?.disableGrossUp ?? false,
  };
};

export const buildInput = (state, initial) => {
  const amount = Number(state.amount);
  const input = {
    code: state.code.trim(),
    description: state.description.trim() || null,
    subtitle: state.subtitle.trim() || null,
    options: [
      {
        amount,
        type: state.amountType,
        priceHike: 0,
      },
    ],
    beforeEventCount: numOrNull(state.beforeEventCount),
    afterEventCount: numOrNull(state.afterEventCount),
    // Start = 12:00am Central on the chosen day; end = 11:59:59pm Central on the
    // chosen day (inclusive), so a promo is live for the whole of both days.
    startsOn: state.startsOn ? moment.tz(state.startsOn, 'YYYY-MM-DD', PROMO_TZ).startOf('day').toISOString() : null,
    endsOn: state.endsOn ? moment.tz(state.endsOn, 'YYYY-MM-DD', PROMO_TZ).endOf('day').toISOString() : null,
    minimumPurchaseAmount: numOrNull(state.minimumPurchaseAmount),
    maximumDiscountAmount: numOrNull(state.maximumDiscountAmount),
    maxUses: numOrNull(state.maxUses),
    disableGrossUp: !!state.disableGrossUp,
  };
  if (initial && initial.id) {
    input.id = initial.id;
  }
  return input;
};

// A small ⓘ affordance that keeps a field's guidance one hover away instead of
// printing it under every input all the time. pointerEvents:auto so it stays
// hoverable even though MUI's InputLabel disables pointer events.
const HelpDot = ({ title }) => (
  <Tooltip title={title} placement="top" arrow enterTouchDelay={0}>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.inactive', cursor: 'help', pointerEvents: 'auto' }}>
      <InfoIcon sx={{ fontSize: 14 }} />
    </Box>
  </Tooltip>
);

// Field label with an optional inline help dot.
const FieldLabel = ({ children, help }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
    {children}
    {help && <HelpDot title={help} />}
  </Box>
);

// Quiet uppercase group heading that splits required from optional.
const SectionLabel = ({ children, help }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '14px' }}>
    <Typography
      sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary' }}
    >
      {children}
    </Typography>
    {help && <HelpDot title={help} />}
  </Box>
);

// While switching promotions the field VALUE area swaps to this skeleton, so the
// values "load in" while the label, border and layout stay exactly in place.
const valueSkeletonProps = { startAdornment: <Skeleton variant="text" width="55%" sx={{ fontSize: '14px' }} /> };

const AmountToggle = ({ value, onChange, disabled }) => (
  <Box
    sx={{
      display: 'flex',
      mt: '8px',
      p: '3px',
      borderRadius: '10px',
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
      backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F0F0F0'),
    }}
  >
    {[
      { key: 'usd', label: '$' },
      { key: 'percent', label: '%' },
    ].map((opt) => (
      <Box
        key={opt.key}
        component="button"
        type="button"
        onClick={() => onChange(opt.key)}
        sx={{
          border: 'none',
          cursor: 'pointer',
          width: '36px',
          height: '30px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 600,
          fontFamily: 'inherit',
          transition: 'background-color 0.15s ease, color 0.15s ease',
          backgroundColor: value === opt.key ? (theme) => theme.palette.background.paper : 'transparent',
          color: (theme) => theme.palette.text.primary,
          boxShadow: value === opt.key ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
        }}
      >
        {opt.label}
      </Box>
    ))}
  </Box>
);

const PromotionForm = ({ initial, resetSignal, onSubmit, submitting, loading = false, submitLabel = 'Create/Update' }) => {
  const [state, setState] = useState(() => deriveState(initial));

  // Re-seed the form when the selected promotion changes (or its data is
  // refetched after save/deactivate). The form itself stays mounted, so labels
  // and layout never flash — only the values change. resetSignal also bumps
  // this when the caller wants a reseed without `initial` itself changing
  // (e.g. clicking +New again while already on a blank/dirty new form).
  useEffect(() => {
    setState(deriveState(initial));
  }, [initial, resetSignal]);

  const set = (key) => (e) => setState((s) => ({ ...s, [key]: e.target.value }));

  const canSubmit =
    state.code.trim() !== '' &&
    state.amount !== '' &&
    Number(state.amount) > 0 &&
    Number(state.maxUses) > 0 &&
    !submitting &&
    !loading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(buildInput(state, initial));
  };

  // Renders a text/number field, or its value-loading skeleton. The label,
  // border and position are identical in both states so nothing moves.
  const textField = ({ field, label, help, placeholder, type, sx: extraSx }) => (
    <TextField
      label={<FieldLabel help={help}>{label}</FieldLabel>}
      fullWidth
      size="small"
      type={type}
      placeholder={loading ? undefined : placeholder}
      value={loading ? '' : state[field]}
      onChange={set(field)}
      disabled={loading}
      InputProps={loading ? valueSkeletonProps : undefined}
      sx={{ ...roundedField, ...extraSx }}
    />
  );

  // Date field, or a matching value-loading skeleton with the same label.
  const dateField = ({ field, label, hint }) =>
    loading ? (
      <TextField label={label} fullWidth size="small" disabled InputProps={valueSkeletonProps} sx={roundedField} />
    ) : (
      <Box>
        <DatePicker
          label={label}
          fullWidth
          size="small"
          value={toDateInput(state[field])}
          onChange={(e) => setState((s) => ({ ...s, [field]: e.target.value }))}
          sx={roundedField}
        />
        {hint && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: '6px', ml: '2px', color: 'text.inactive', lineHeight: 1.4 }}
          >
            {hint}
          </Typography>
        )}
      </Box>
    );

  return (
    <Stack spacing="24px">
      {/* Required */}
      <Box>
        <SectionLabel>Required</SectionLabel>
        <Stack spacing="18px">
          {textField({
            field: 'code',
            label: 'Code',
            help: 'The code riders enter. Must be unique. Stored lowercase.',
            placeholder: 'DISCOUNT20',
          })}
          <Box sx={twoColGridSx}>
            <Box display="flex" gap="8px" alignItems="flex-start">
              {textField({
                field: 'amount',
                label: 'Amount',
                help: state.amountType === 'usd' ? 'Dollars off the fare' : 'Percent off the fare',
                type: 'number',
                sx: { flex: 1 },
              })}
              <AmountToggle
                value={state.amountType}
                disabled={loading}
                onChange={(v) => setState((s) => ({ ...s, amountType: v }))}
              />
            </Box>
            {textField({ field: 'maxUses', label: 'Max uses', help: 'Per-rider redemption limit', type: 'number' })}
          </Box>
        </Stack>
      </Box>

      <Divider />

      {/* Optional */}
      <Box>
        <SectionLabel>Optional</SectionLabel>
        <Stack spacing="18px">
          {textField({ field: 'description', label: 'Description', placeholder: 'A discount code for our Winter promotion.' })}
          {textField({ field: 'subtitle', label: 'Subtitle', placeholder: 'Shown under the code in the app.' })}
          <Box sx={twoColGridSx}>
            {dateField({
              field: 'startsOn',
              label: 'Starts on',
              hint: (
                <>
                  <Box component="span" sx={{ fontWeight: 600 }}>Inclusive.</Box> Live from 12:00am Central on this day.
                </>
              ),
            })}
            {dateField({
              field: 'endsOn',
              label: 'Ends on',
              hint: (
                <>
                  <Box component="span" sx={{ fontWeight: 600 }}>Inclusive.</Box> Live through 11:59pm Central on this day.
                </>
              ),
            })}
            {textField({
              field: 'minimumPurchaseAmount',
              label: 'Min purchase amount',
              help: 'Fare must be at least this',
              type: 'number',
            })}
            {textField({
              field: 'maximumDiscountAmount',
              label: 'Max discount amount',
              help: 'Optional cap on the discount',
              type: 'number',
            })}
          </Box>

          {/* Ride-count window — a labelled pair, not a filled "well", so it
              sits flush with the rest of the flat fields. */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '10px' }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>Ride-count window</Typography>
              <HelpDot title="Exclusive bounds on the rider's completed rides. Valid while completed rides ≥ After and the current ride is < Before. Example: first 3 rides → After = 0, Before = 4. Leave blank for no limit." />
            </Box>
            <Box sx={twoColGridSx}>
              {textField({ field: 'afterEventCount', label: 'After event', type: 'number' })}
              {textField({ field: 'beforeEventCount', label: 'Before event', type: 'number' })}
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={!!state.disableGrossUp}
                disabled={loading}
                onChange={(e) =>
                  setState((s) => ({ ...s, disableGrossUp: e.target.checked }))
                }
              />
            }
            label={
              <FieldLabel help="On: the promo comes off the fare as a real discount. Off (default): the fare is grossed up by the promo amount so the discount nets to zero. Overrides pricing config.">
                Apply as real discount (disable gross-up)
              </FieldLabel>
            }
          />
        </Stack>
      </Box>

      <Button
        variant="contained"
        color="primary"
        disabled={!canSubmit}
        loading={submitting}
        onClick={handleSubmit}
        sx={{
          borderRadius: '10px',
          py: '10px',
          fontWeight: 600,
          fontSize: '15px',
          alignSelf: 'flex-start',
          px: '32px',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover:not(:disabled)': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        {submitLabel}
      </Button>
    </Stack>
  );
};

export default PromotionForm;
